import React, {useEffect, useRef, useState} from 'react';
import {Input} from '@packages/systems/design-system/Input/Input';
import trim from 'lodash/trim';
import {
  request,
  type VideoHelperResponse,
} from '@packages/systems/core/utils/EmbedlyUtils';
import {Note} from '@packages/systems/spring';
import {labelStyle} from '@packages/systems/ExpressionEditor-utils-ui';
import {emotionStyled} from '@packages/utilities/styled';
import {rightSidebarLabelWidth} from '@packages/systems/design-system/constants/DesignerUI';
const YOUTUBE_FAIL_TITLE = '- YouTube';

const InputLoadingIndicator = emotionStyled('div')(
  {
    display: 'none',
    width: 6,
    height: 6,
    margin: 'auto $1 auto $2',
    borderRadius: '$rounded',
    background: '$background1',
  },
  {
    variants: {
      loading: {
        true: {
          display: 'block',
        },
      },
    },
  }
);

const LabelInputRow = emotionStyled('div')({
  display: 'grid',
  gridTemplateColumns: `${rightSidebarLabelWidth}px minmax(0, auto)`,
  gridTemplateRows: 'minmax($2, auto)',
  gridGap: '$2',
  alignItems: 'center',
  marginBlockEnd: '$2',
});

const ErrorNote = emotionStyled(Note)({
  marginBlockStart: '$-1',
  gridColumn: 2,
});

const Label = emotionStyled('label')(labelStyle);

const fetchEmbedly = async (
  inputValue: string,
  canceledRef: {
    current: boolean;
  },
  end: () => void,
  start: () => void,
  // @ts-expect-error - TS7006 - Parameter 'commit' implicitly has an 'any' type.
  commit,
  fail: () => void
) => {
  start();
  // Reset the canceledRef whenever fetch is called
  canceledRef.current = false;

  try {
    const response: VideoHelperResponse = await request(inputValue);
    // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'false' and 'true' have no overlap.
    if (canceledRef.current === true) {
      return end();
    }
    commit(response);
    // If the user gives a link with an invalid video id Embedly will not fail the call,
    // check for undefined metadata information to catch these cases.
    // Youtube now returns this generic desc if the video is not found
    if (
      !response.metadata?.title ||
      response.metadata?.title === YOUTUBE_FAIL_TITLE
    ) {
      fail();
    }
  } catch (err: any) {
    fail();
  }
};

const YouTubeUrlTypeRender = ({
  url,
  title,
  updateMetaExpr,
  updateTitle,
}: {
  url: string;
  title: string;
  updateMetaExpr: (arg1: VideoHelperResponse) => void;
  updateTitle: (arg1: string) => void;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [localTitle, setTitle] = useState(title);

  useEffect(() => {
    title && setTitle(title);
  }, [title]);

  useEffect(() => {
    url && setInputValue(url);
  }, [url]);

  const canceledRef = useRef(false);

  const end = () => {
    canceledRef.current = false;
    setLoading(false);
  };
  // @ts-expect-error - TS7006 - Parameter 'response' implicitly has an 'any' type.
  const commit = (response) => {
    updateMetaExpr(response);
    end();
  };
  const fail = () => {
    setError('Invalid YouTube video link');
    end();
  };
  const start = () => {
    canceledRef.current = true;
    // @ts-expect-error - TS2345 - Argument of type 'boolean' is not assignable to parameter of type 'SetStateAction<string>'.
    setError(false);
    setLoading(true);
  };
  const queryUrl = () => {
    const trimmedValue = trim(inputValue);
    if (trimmedValue && url !== trimmedValue) {
      fetchEmbedly(inputValue, canceledRef, end, start, commit, fail);
    } else if (trimmedValue === '') {
      start();
      commit({url: ''});
      end();
    }
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      queryUrl();
    }
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // @ts-expect-error - TS2345 - Argument of type 'boolean' is not assignable to parameter of type 'SetStateAction<string>'.
    setError(false);
    setInputValue(e.target.value);
  };

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };
  const onEnterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      updateTitle(localTitle);
    }
  };
  const onTitleBlur = () => {
    updateTitle(localTitle);
  };

  return (
    <>
      <LabelInputRow>
        <Label
          htmlFor="youtube-url-input"
          data-automation-id="symbols-dataType-contentType"
        >
          URL
        </Label>
        <Input
          id="youtube-url-input"
          value={inputValue}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={queryUrl}
          placeholder="e.g. https://youtu.be/Ojiv9Smi4XE"
          data-automation-id="opt-youtube-url"
          suffix={
            <InputLoadingIndicator
              data-automation-id="youtube-video-field-validation-status-indicator"
              data-status={loading && 'LOADING'}
              className={loading ? 'animate-blink' : ''}
              loading={loading}
            />
          }
        />
        {error && (
          <ErrorNote colorVariant="danger" hasIcon={false}>
            {error}
          </ErrorNote>
        )}
        <Label htmlFor="youtube-title-input">Title</Label>
        <Input
          id="youtube-title-input"
          onBlur={onTitleBlur}
          onKeyDown={onEnterKeyDown}
          value={localTitle}
          onChange={onTitleChange}
          data-automation-id="opt-youtube-title"
        />
      </LabelInputRow>
    </>
  );
};

export default YouTubeUrlTypeRender;
